/**
 * Fetches a project's prospects (the candidates listed on the project-details
 * page) via TanStack Query, replacing the legacy `projectDetailsApiActions`
 * Redux thunk for this read.
 *
 * GET /api/v1/projects/:projectId/list_prospects
 *
 * The endpoint returns the paginated envelope `{ data: { prospects,
 * metrics_count }, meta }`, so we pass `unwrapData: false` to keep `meta`
 * (pagination) — the same shape the page already consumes.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/api/request';

export interface ProjectProspectsMeta {
  page?: number;
  pages?: number;
  count?: number;
  items?: number;
}

export interface ProjectProspectsResponse {
  data: {
    prospects: any[];
    metrics_count: Record<string, number>;
  };
  meta: ProjectProspectsMeta;
}

export const PROJECT_PROSPECTS_QUERY_KEY = 'project-prospects';

const fetchProjectProspects = (
  projectId: string,
  params: Record<string, unknown>
): Promise<ProjectProspectsResponse> =>
  api.get<ProjectProspectsResponse>(`projects/${projectId}/list_prospects`, {
    client: 'api',
    params,
    // Keep the full envelope so `meta` (pagination) survives.
    unwrapData: false,
  });

export function useGetProjectProspects(
  projectId: string,
  params: Record<string, unknown>,
  options?: { enabled?: boolean }
): UseQueryResult<ProjectProspectsResponse, Error> {
  return useQuery({
    queryKey: [PROJECT_PROSPECTS_QUERY_KEY, projectId, params],
    queryFn: () => fetchProjectProspects(projectId, params),
    enabled: (options?.enabled ?? true) && !!projectId,
    staleTime: 0,
  });
}
